import React, { useEffect } from "react";
import { Box, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px 20px",
  [theme.breakpoints.down("md")]: {
    padding: "32px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 8px",
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "0px",
  boxSizing: "border-box",
  padding: "32px",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  border: "1px solid #05495a",
  boxShadow: "0px 4px 24px 0px rgba(10,130,157,0.1)",
  [theme.breakpoints.down("md")]: {
    gap: "0px",
    padding: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "0px",
    padding: "12px",
    borderRadius: "12px",
    maxWidth: "95%",
  },
}));

const BackButton = styled("button")(({ theme }) => ({
  alignSelf: "flex-start",
  background: "none",
  border: "none",
  color: "#0a829d",
  fontSize: "16px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 0",
  fontFamily: "Rethink Sans, sans-serif",
  "&:hover": {
    textDecoration: "underline",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    padding: "6px 0",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  textAlign: "center",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    fontSize: "24px",
    lineHeight: "30px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
    lineHeight: "26px",
    marginBottom: "6px",
  },
}));

const LastUpdated = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#737373",
  textAlign: "center",
  marginBottom: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "13px",
    marginBottom: "12px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    marginBottom: "10px",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "24px",
  color: "#033843",
  marginBottom: "8px",
  marginTop: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "16px",
    lineHeight: "22px",
    marginTop: "14px",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "15px",
    lineHeight: "20px",
    marginTop: "12px",
    marginBottom: "6px",
  },
}));

const SectionContent = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
  marginBottom: "8px",
  wordBreak: "normal",
  overflowWrap: "break-word",
  hyphens: "none",
  whiteSpace: "normal",
  [theme.breakpoints.down("md")]: {
    fontSize: "13px",
    lineHeight: "18px",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    lineHeight: "16px",
    marginBottom: "6px",
  },
}));

const BulletList = styled(Box)(({ theme }) => ({
  paddingLeft: "20px",
  marginBottom: "8px",
  "& li": {
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "14px",
    lineHeight: "20px",
    color: "#262626",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("md")]: {
    paddingLeft: "18px",
    marginBottom: "6px",
    "& li": {
      fontSize: "13px",
      lineHeight: "18px",
      marginBottom: "4px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    paddingLeft: "16px",
    marginBottom: "6px",
    "& li": {
      fontSize: "12px",
      lineHeight: "16px",
      marginBottom: "4px",
    },
  },
}));

const ContactInfo = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #bfe5ec",
  borderRadius: "8px",
  padding: "20px",
  marginTop: "16px",
  "& p": {
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "14px",
    lineHeight: "20px",
    color: "#262626",
    margin: "3px 0",
  },
  [theme.breakpoints.down("md")]: {
    padding: "16px",
    marginTop: "14px",
    "& p": {
      fontSize: "13px",
      lineHeight: "18px",
      margin: "2px 0",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px",
    marginTop: "12px",
    borderRadius: "6px",
    "& p": {
      fontSize: "12px",
      lineHeight: "16px",
      margin: "2px 0",
    },
  },
}));

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <PageContainer>
      <ContentContainer>
        <BackButton onClick={handleBack}>
          ← Back
        </BackButton>
        
        <Title>UniComply.ai Privacy Policy</Title>
        <LastUpdated>Last updated: September 07, 2025</LastUpdated>

        <SectionContent>
          At UniComply.ai, we value your privacy. This Privacy Policy explains how we collect, use, disclose, and protect information when you use our compliance hub and related services.
        </SectionContent>

        <SectionContent>
          By using our Services, you agree to the practices described here.
        </SectionContent>

        <SectionTitle>Information We Collect</SectionTitle>
        <SectionContent>
          We collect the following categories of information:
        </SectionContent>
        <BulletList>
          <li>Personal information you provide, such as your name, email, and login credentials.</li>
          <li>Account data, including subscription details and training assignments.</li>
          <li>Usage data, such as IP address, browser type, device identifiers, and activity logs.</li>
          <li>Content you submit, including policy templates, compliance documents, or survey responses.</li>
          <li>Payment details (if you subscribe to paid services), processed securely by third-party providers.</li>
        </BulletList>

        <SectionTitle>How We Use Your Information</SectionTitle>
        <SectionContent>
          We use your information to:
        </SectionContent>
        <BulletList>
          <li>Provide, operate, and improve the Services.</li>
          <li>Track compliance activities, assignments, and audit results.</li>
          <li>Communicate with you about your account, updates, or new features.</li>
          <li>Personalize training and compliance recommendations.</li>
          <li>Ensure security, prevent fraud, and comply with legal obligations.</li>
        </BulletList>

        <SectionTitle>How We Share Your Information</SectionTitle>
        <SectionContent>
          We may share your information with:
        </SectionContent>
        <BulletList>
          <li>Service providers assisting in hosting, payment processing, or analytics.</li>
          <li>Regulatory authorities or auditors if legally required.</li>
          <li>Affiliates and partners for business operations.</li>
        </BulletList>
        <SectionContent>
          We do not sell or rent your personal information.
        </SectionContent>

        <SectionTitle>Cookies and Tracking Technologies</SectionTitle>
        <SectionContent>
          UniComply.ai uses cookies, beacons, and tracking tools to improve user experience and analyze usage.
        </SectionContent>
        <BulletList>
          <li>You may disable cookies in your browser, but some features may not function properly.</li>
        </BulletList>

        <SectionTitle>Data Security and Retention</SectionTitle>
        <SectionContent>
          We use technical, organizational, and administrative safeguards to protect your information.
        </SectionContent>
        <BulletList>
          <li>While no system is 100% secure, we continuously monitor and update our security measures.</li>
          <li>We retain your data as long as necessary to provide services or comply with legal obligations.</li>
        </BulletList>

        <SectionTitle>International Data Transfers</SectionTitle>
        <SectionContent>
          Your information may be transferred and stored in the United States and other countries where we operate.
        </SectionContent>
        <BulletList>
          <li>We ensure adequate safeguards, such as contractual protections, for international data transfers.</li>
        </BulletList>

        <SectionTitle>Your Rights and Choices</SectionTitle>
        <SectionContent>
          Depending on your location, you may have rights such as:
        </SectionContent>
        <BulletList>
          <li>Accessing or correcting your personal data.</li>
          <li>Requesting deletion of your data.</li>
          <li>Opting out of marketing communications.</li>
          <li>Withdrawing consent for certain processing.</li>
        </BulletList>
        <SectionContent>
          To exercise these rights, contact us at support@affableone.ai.
        </SectionContent>

        <SectionTitle>Children's Privacy</SectionTitle>
        <SectionContent>
          UniComply.ai Services are not directed to children under 13, and we do not knowingly collect personal information from them.
        </SectionContent>
        <BulletList>
          <li>If we become aware of such data, we will delete it promptly.</li>
        </BulletList>

        <SectionTitle>Third-Party Links</SectionTitle>
        <SectionContent>
          Our Services may include links to third-party websites. We are not responsible for their privacy practices.
        </SectionContent>
        <BulletList>
          <li>Please review their policies before providing personal information.</li>
        </BulletList>

        <SectionTitle>Changes to This Privacy Policy</SectionTitle>
        <SectionContent>
          We may update this Privacy Policy periodically. Updates will be posted with a new "Last Updated" date.
        </SectionContent>
        <BulletList>
          <li>Continued use of the Services after updates constitutes acceptance of the revised Privacy Policy.</li>
        </BulletList>

        <SectionTitle>Contact Us</SectionTitle>
        <ContactInfo>
          <Typography component="p">
            If you have questions about this Privacy Policy, please contact us at:
          </Typography>
          <Typography component="p">
            UniComply.ai, Sammamish, WA 98101, United States
          </Typography>
          <Typography component="p">
            Email: support@affableone.ai
          </Typography>
        </ContactInfo>
      </ContentContainer>
    </PageContainer>
  );
}